import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ADMIN_PATH } from "../../config/adminPath";
import api from "../../core/api/api";
import useLiveSync from "../hooks/useLiveSync";
import { formatAmount as fmt } from "../../utils/common/money";   // single-source ₹ format

export default function AdminHeader({ orders = [], orderStats = null, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isB2B    = location.pathname.includes("/b2b");
  const isSeller = !isB2B && location.pathname.includes("/sellers");
  const wsLabel  = isB2B ? "B2B Wholesale" : isSeller ? "Seller Hub" : "Admin Panel";
  const wsGrad   = isB2B
    ? "linear-gradient(135deg,#0f172a,#475569)"
    : isSeller
    ? "linear-gradient(135deg,#059669,#34d399)"
    : "linear-gradient(135deg,#6366f1,#8b5cf6)";
  const [showNotif, setShowNotif] = useState(false);
  const [activeTab, setActiveTab] = useState("orders"); // "orders" | "seller"
  const [lastSeenCount, setLastSeenCount] = useState(() => {
    return Number(localStorage.getItem("admin_last_seen_orders") || 0);
  });
  const dropRef = useRef(null);

  // Seller activity notifications
  const [sellerNotifs,      setSellerNotifs]      = useState([]);
  const [sellerUnread,      setSellerUnread]      = useState(0);
  const [loadingSellerNotif,setLoadingSellerNotif] = useState(false);

  const fetchSellerNotifs = useCallback(async () => {
    try {
      setLoadingSellerNotif(true);
      const res = await api.get("/admin-notifications?limit=30");
      setSellerNotifs(res.data.notifications || []);
      setSellerUnread(res.data.unreadCount || 0);
    } catch {}
    finally { setLoadingSellerNotif(false); }
  }, []);

  // initial load + single-source smart polling (pauses when tab hidden, refreshes on focus)
  useEffect(() => { fetchSellerNotifs(); }, [fetchSellerNotifs]);
  useLiveSync(fetchSellerNotifs, { intervalMs: 30000 });

  /* Pending = CREATED or PAID orders */
  const pendingOrders = orders.filter(o => o.status === "CREATED" || o.status === "PAID");
  const newCount = Math.max(0, pendingOrders.length - lastSeenCount);

  /* Total badge = order alerts + seller unread */
  const totalBadge = newCount + sellerUnread;

  /* Close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setShowNotif(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleBellClick = () => {
    setShowNotif(v => !v);
    if (!showNotif) {
      setLastSeenCount(pendingOrders.length);
      localStorage.setItem("admin_last_seen_orders", String(pendingOrders.length));
      fetchSellerNotifs();
    }
  };

  const markSellerNotifRead = async (notif) => {
    if (notif.isRead) return;
    try {
      await api.patch(`/admin-notifications/${notif._id}/read`);
      setSellerNotifs(prev => prev.map(n => n._id === notif._id ? { ...n, isRead: true } : n));
      setSellerUnread(c => Math.max(0, c - 1));
    } catch {}
  };

  const markAllSellerRead = async () => {
    try {
      await api.patch("/admin-notifications/read-all");
      setSellerNotifs(prev => prev.map(n => ({ ...n, isRead: true })));
      setSellerUnread(0);
    } catch {}
  };

  const fmtTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1)   return "Just now";
    if (diffMin < 60)  return `${diffMin}m ago`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24)    return `${diffH}h ago`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  return (
    <div style={{
      height: 58, background: "#fff", borderBottom: "1px solid #e2e8f0",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 24px", position: "sticky", top: 0, zIndex: 100,
      boxShadow: "0 1px 8px rgba(0,0,0,0.06)"
    }}>
      {/* Left — brand + workspace switch */}
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: wsGrad, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: 14 }}>V</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, color: "#0f172a", lineHeight: 1 }}>VARAii</div>
            <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600 }}>{wsLabel}</div>
          </div>
        </div>

        {/* Admin ⇄ Seller ⇄ B2B workspace switch */}
        <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
          {[
            { key: "admin",  label: "🛍️ Admin",  to: `${ADMIN_PATH}/dashboard`,     active: !isB2B && !isSeller },
            { key: "seller", label: "🏪 Seller", to: `${ADMIN_PATH}/sellers/dashboard`, active: isSeller },
            { key: "b2b",    label: "🏢 B2B",    to: `${ADMIN_PATH}/b2b/dashboard`, active: isB2B },
          ].map(t => (
            <button key={t.key} onClick={() => navigate(t.to)} style={{
              padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 700,
              background: t.active ? "#fff" : "transparent",
              color: t.active ? "#0f172a" : "#94a3b8",
              boxShadow: t.active ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
              transition: "all 0.15s"
            }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Right — stats + bell + logout */}
      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>

        {/* Live order stats — accurate counts from /orders/stats (fall back to
            the loaded list until stats arrive). */}
        <div style={{ display: "flex", gap: 14 }}>
          {[
            { label: "Pending",   value: orderStats ? (orderStats.created + orderStats.paid) : pendingOrders.length,        color: "#1d4ed8", bg: "#eff6ff" },
            { label: "Shipped",   value: orderStats ? orderStats.shipped   : orders.filter(o => o.status === "SHIPPED").length,   color: "#7c3aed", bg: "#faf5ff" },
            { label: "Delivered", value: orderStats ? orderStats.delivered : orders.filter(o => o.status === "DELIVERED").length, color: "#15803d", bg: "#f0fdf4" },
          ].map(s => (
            <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20, background: s.bg, cursor: "pointer" }}
              onClick={() => navigate(`${ADMIN_PATH}/orders`)}>
              <span style={{ fontSize: 16, fontWeight: 900, color: s.color }}>{s.value}</span>
              <span style={{ fontSize: 10, color: s.color, fontWeight: 600 }}>{s.label}</span>
            </div>
          ))}
        </div>

        <div style={{ width: 1, height: 28, background: "#e2e8f0" }} />

        {/* 🔔 Notification Bell */}
        <div style={{ position: "relative" }} ref={dropRef}>
          <button onClick={handleBellClick} style={{
            width: 38, height: 38, borderRadius: "50%", border: "none",
            background: totalBadge > 0 ? "#eff6ff" : "#f8fafc",
            cursor: "pointer", fontSize: 18, position: "relative",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: totalBadge > 0 ? "0 0 0 2px #bfdbfe" : "none"
          }}>
            🔔
            {totalBadge > 0 && (
              <span style={{
                position: "absolute", top: 0, right: 0,
                width: 18, height: 18, borderRadius: "50%",
                background: "#dc2626", color: "#fff",
                fontSize: 10, fontWeight: 900,
                display: "flex", alignItems: "center", justifyContent: "center",
                border: "2px solid #fff"
              }}>
                {totalBadge > 9 ? "9+" : totalBadge}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotif && (
            <div style={{
              position: "absolute", right: 0, top: 46, width: 400,
              background: "#fff", borderRadius: 16,
              boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
              border: "1px solid #e2e8f0", zIndex: 200, overflow: "hidden"
            }}>
              {/* Tabs */}
              <div style={{ display: "flex", borderBottom: "1px solid #f1f5f9" }}>
                {[
                  { key: "orders", label: "📦 Orders", count: newCount },
                  { key: "seller", label: "🏪 Seller Activity", count: sellerUnread }
                ].map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
                    flex: 1, padding: "12px 8px", border: "none", cursor: "pointer",
                    background: activeTab === tab.key ? "#fff" : "#f8fafc",
                    fontWeight: activeTab === tab.key ? 800 : 600,
                    color: activeTab === tab.key ? "#0f172a" : "#94a3b8",
                    fontSize: 12, borderBottom: activeTab === tab.key ? "2px solid #6366f1" : "2px solid transparent",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6
                  }}>
                    {tab.label}
                    {tab.count > 0 && (
                      <span style={{ background: "#dc2626", color: "#fff", fontSize: 10, fontWeight: 900, padding: "1px 6px", borderRadius: 12 }}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* ── ORDERS TAB ── */}
              {activeTab === "orders" && (
                <>
                  <div style={{ maxHeight: 340, overflowY: "auto" }}>
                    {pendingOrders.length === 0 ? (
                      <div style={{ padding: "30px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                        ✅ All orders are up to date!
                      </div>
                    ) : (
                      pendingOrders.slice(0, 10).map(o => (
                        <div key={o._id}
                          onClick={() => { navigate(`${ADMIN_PATH}/orders/${o._id}`); setShowNotif(false); }}
                          style={{ padding: "12px 16px", borderBottom: "1px solid #f8fafc", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "background 0.1s" }}
                          onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 13, color: "#0f172a", fontFamily: "monospace" }}>#{o.orderNo}</div>
                            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                              {o.customerName} · {o.items?.length} item{o.items?.length !== 1 ? "s" : ""}
                            </div>
                            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                              {new Date(o.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontWeight: 800, fontSize: 14, color: "#0f172a" }}>₹{fmt(o.grandTotal)}</div>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: o.status === "PAID" ? "#f0fdf4" : "#eff6ff", color: o.status === "PAID" ? "#15803d" : "#1d4ed8" }}>
                              {o.status}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div style={{ padding: "10px 16px", borderTop: "1px solid #f1f5f9", textAlign: "center" }}>
                    <button onClick={() => { navigate(`${ADMIN_PATH}/orders`); setShowNotif(false); }}
                      style={{ background: "none", border: "none", color: "#6366f1", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      View all orders →
                    </button>
                  </div>
                </>
              )}

              {/* ── SELLER ACTIVITY TAB ── */}
              {activeTab === "seller" && (
                <>
                  <div style={{ padding: "10px 16px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                      {sellerUnread > 0 ? `${sellerUnread} unread` : "All caught up!"}
                    </span>
                    {sellerUnread > 0 && (
                      <button onClick={markAllSellerRead} style={{ background: "none", border: "none", color: "#6366f1", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        Mark all read
                      </button>
                    )}
                  </div>

                  <div style={{ maxHeight: 340, overflowY: "auto" }}>
                    {loadingSellerNotif ? (
                      <div style={{ padding: "30px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Loading…</div>
                    ) : sellerNotifs.length === 0 ? (
                      <div style={{ padding: "30px 16px", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
                        🏪 No seller activity yet
                      </div>
                    ) : (
                      sellerNotifs.map(n => (
                        <div key={n._id}
                          onClick={() => markSellerNotifRead(n)}
                          style={{
                            padding: "12px 16px", borderBottom: "1px solid #f8fafc", cursor: "default",
                            background: n.isRead ? "transparent" : "#fefce8",
                            transition: "background 0.1s"
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 13, fontWeight: n.isRead ? 600 : 800, color: "#0f172a", marginBottom: 3 }}>
                                {n.title}
                              </div>
                              <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>{n.message}</div>
                              <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
                                🏪 {n.sellerId?.shopName || n.meta?.sellerName || "Seller"} · {fmtTime(n.createdAt)}
                              </div>
                            </div>
                            {!n.isRead && (
                              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", flexShrink: 0, marginTop: 4 }} />
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Logout */}
        <button onClick={onLogout} style={{
          padding: "7px 16px", borderRadius: 10, border: "1.5px solid #fee2e2",
          background: "#fef2f2", color: "#dc2626", fontSize: 12, fontWeight: 700, cursor: "pointer"
        }}>
          Logout
        </button>
      </div>
    </div>
  );
}
